// src/data/weeklyQuiz.js

export const WEEKLY_QUIZ = {
  id: 'weekly-third-conditional-1',
  title: 'Third Conditional: Travel, Nature & Regrets',
  description:
    'Practise the third conditional to talk about past travel problems, endangered animals and personal regrets.',
  totalQuestions: 30,
  estimatedMinutes: 12,
  isActive: true
};

// Alias de compatibilidad
export const WEEKLY_CHALLENGE = WEEKLY_QUIZ;

export const WEEKLY_UNIT = {
  id: 'b1-b2-third-conditional',
  title: 'Unit: If Things Had Been Different',
  iconType: 'globe',
  grammarText:
    'Unit focus: Third conditional — If + past perfect, would have + past participle.',
  sets: [
    {
      id: 'travel-set1',
      title: 'Set 1: Travel Problems (Reading & Context)',
      context: `*Problemas de viaje*


Estás leyendo mensajes de viajeros que tuvieron problemas durante sus vacaciones.
Tu objetivo: Usar el tercer condicional para imaginar cómo la situación habría sido diferente.


*Travel Problems*


You are reading messages from travellers who had problems during their holidays.
Your goal: Use the third conditional to imagine how the situation could have been different.`,
      questions: [
        {
          type: 'mc',
          q: "Message 1: 'If we _____ earlier, we wouldn't have missed our flight.'",
          options: ['had left', 'left', 'would leave'],
          a: 'had left',
          tip: "Use 'If + past perfect' in the if-clause: 'If we had left earlier'."
        },
        {
          type: 'mc',
          q: "Message 2: 'If the airline hadn't overbooked the flight, we _____ a seat.'",
          options: ['would have got', 'will get', 'would get'],
          a: 'would have got',
          tip: "Use 'would have + past participle' in the main clause."
        },
        {
          type: 'match',
          q: 'Match each travel problem with its third conditional result:',
          pairs: [
            { left: 'We missed the flight.', right: 'If we had arrived earlier, we would have caught it.' },
            { left: 'Our luggage was lost.', right: 'If the airline had checked it properly, it wouldn\'t have disappeared.' },
            { left: 'The hotel was overbooked.', right: 'If we had confirmed the booking, we would have had a room.' },
            { left: 'The tour was delayed.', right: 'If the guide had been on time, we wouldn\'t have waited for hours.' }
          ],
          a: 'completed',
          successTip: 'Great! You can now connect past problems with imaginary results. ✈️'
        },
        {
          type: 'mc',
          q: "Message 3: 'If I _____ the booking details, I wouldn't have made a mistake with the dates.'",
          options: ['had checked', 'checked', 'would have checked'],
          a: 'had checked',
          tip: "In the if-clause, use the past perfect: 'had checked'."
        },
        {
          type: 'mc',
          q: "Message 4: 'If we hadn't spent so long sightseeing, we _____ the last train.'",
          options: ["wouldn't have missed", "didn't miss", "won't miss"],
          a: "wouldn't have missed",
          tip: "For a negative imaginary result, use 'wouldn't have + past participle'."
        },
        {
          type: 'input',
          q: "Message 5: 'If the airline had informed us about the delay, we _____ waited at the airport all night.'",
          a: "wouldn't have",
          alts: ['would not have'],
          tip: "Use 'wouldn't have waited' for a negative imaginary result.",
          successTip: 'Correct! “Wouldn’t have waited” expresses an unreal past result. 🕐'
        },
        {
          type: 'match',
          q: 'Match the travel vocabulary with its definition:',
          pairs: [
            { left: 'Missed flight', right: 'A flight you did not take because you arrived too late' },
            { left: 'Lost luggage', right: 'Bags that the airline cannot find' },
            { left: 'Booking mistake', right: 'An error in a hotel, flight or tour reservation' },
            { left: 'Overbooked', right: 'When an airline sells more seats than are available' }
          ],
          a: 'completed',
          successTip: 'Excellent travel vocabulary! 🧳'
        },
        {
          type: 'mc',
          q: "Message 6: 'If we _____ a taxi, we would have arrived at the airport on time.'",
          options: ['had taken', 'took', 'would take'],
          a: 'had taken',
          tip: "Use 'If + subject + had + past participle' to talk about an unreal past action."
        },
        {
          type: 'mc',
          q: 'Which sentence is grammatically CORRECT?',
          options: [
            'If we had known about the delay, we would have stayed at the hotel.',
            'If we would have known about the delay, we had stayed at the hotel.',
            'If we knew about the delay, we would have stayed at the hotel.'
          ],
          a: 'If we had known about the delay, we would have stayed at the hotel.',
          tip: "The third conditional is: 'If + had + past participle, would have + past participle'."
        },
        {
          type: 'mc',
          q: "Message 7: 'If the hotel hadn't lost our reservation, we _____ a stressful first night.'",
          options: ["wouldn't have had", "didn't have", "wouldn't have"],
          a: "wouldn't have had",
          tip: "After 'wouldn't have', use the past participle: 'had'."
        }
      ]
    },
    {
      id: 'nature-set2',
      title: 'Set 2: Endangered Species (Word Order & Syntax)',
      context: `*Animales en peligro de extinción*


Un documental explica cómo las decisiones humanas han afectado a los animales en peligro.
Tu objetivo: Ordenar frases con el tercer condicional para hablar sobre conservación.


*Endangered Species*


A documentary explains how human decisions have affected endangered animals.
Your goal: Put the words in the correct order and build accurate third conditional sentences.`,
      questions: [
        {
          type: 'order',
          q: 'Si los humanos no hubieran destruido su hábitat, los animales no habrían desaparecido.',
          words: ['If', 'humans', "hadn't", 'destroyed', 'their', 'habitat,', 'the', 'animals', "wouldn't", 'have', 'disappeared.'],
          a: "If humans hadn't destroyed their habitat, the animals wouldn't have disappeared.",
          extra: ['did', 'not'],
          tip: "Use 'hadn't + past participle' in the if-clause and 'wouldn't have + past participle' in the result."
        },
        {
          type: 'order',
          q: 'Si los guardabosques hubieran protegido a los elefantes, los cazadores furtivos no habrían matado a tantos.',
          words: ['If', 'rangers', 'had', 'protected', 'the', 'elephants,', 'poachers', "wouldn't", 'have', 'killed', 'so', 'many.'],
          a: "If rangers had protected the elephants, poachers wouldn't have killed so many.",
          extra: ['would', 'kill'],
          tip: "The if-clause needs 'had protected'; the result needs 'wouldn't have killed'."
        },
        {
          type: 'order',
          q: 'Si la gente hubiera donado más dinero, el santuario habría rescatado a más animales.',
          words: ['If', 'people', 'had', 'donated', 'more', 'money,', 'the', 'sanctuary', 'would', 'have', 'rescued', 'more', 'animals.'],
          a: 'If people had donated more money, the sanctuary would have rescued more animals.',
          extra: ['would', 'donate'],
          tip: "Use 'If + had + past participle, would have + past participle'."
        },
        {
          type: 'order',
          q: 'Si no hubiéramos contaminado los océanos, muchas especies no habrían estado en peligro.',
          words: ['If', 'we', "hadn't", 'polluted', 'the', 'oceans,', 'many', 'species', "wouldn't", 'have', 'been', 'endangered.'],
          a: "If we hadn't polluted the oceans, many species wouldn't have been endangered.",
          extra: ['did', 'not'],
          tip: "The past participle of 'pollute' is 'polluted'."
        },
        {
          type: 'match',
          q: 'Match each conservation action with its third conditional result:',
          pairs: [
            { left: 'Protect the forest.', right: 'If people had protected the forest, the animals would have survived.' },
            { left: 'Stop poaching.', right: 'If poaching had stopped, the rhinos would not have disappeared.' },
            { left: 'Create a sanctuary.', right: 'If they had created a sanctuary, more animals would have been safe.' },
            { left: 'Reduce plastic waste.', right: 'If we had reduced plastic waste, the oceans would have been cleaner.' }
          ],
          a: 'completed',
          successTip: 'Perfect! You can talk about conservation and imaginary past results. 🐾'
        },
        {
          type: 'order',
          q: 'Si los científicos hubieran encontrado una solución antes, la especie no se habría extinguido.',
          words: ['If', 'scientists', 'had', 'found', 'a', 'solution', 'earlier,', 'the', 'species', "wouldn't", 'have', 'become', 'extinct.'],
          a: "If scientists had found a solution earlier, the species wouldn't have become extinct.",
          extra: ['would', 'find'],
          tip: "Use 'had found' in the condition and 'wouldn't have become' in the result."
        },
        {
          type: 'order',
          q: 'Si el gobierno hubiera creado más áreas protegidas, la pérdida de hábitat habría sido menor.',
          words: ['If', 'the', 'government', 'had', 'created', 'more', 'protected', 'areas,', 'habitat', 'loss', 'would', 'have', 'been', 'smaller.'],
          a: 'If the government had created more protected areas, habitat loss would have been smaller.',
          extra: ['would', 'create'],
          tip: "'Habitat loss' is the subject of the imaginary result."
        },
        {
          type: 'order',
          q: 'Si hubiéramos aprendido más sobre conservación, habríamos ayudado a los animales.',
          words: ['If', 'we', 'had', 'learned', 'more', 'about', 'conservation,', 'we', 'would', 'have', 'helped', 'the', 'animals.'],
          a: 'If we had learned more about conservation, we would have helped the animals.',
          extra: ['would', 'learn'],
          tip: "Use the past participle 'learned' or 'learnt' in the if-clause."
        },
        {
          type: 'order',
          q: 'Si los turistas no hubieran comprado productos de animales, la caza furtiva habría disminuido.',
          words: ['If', 'tourists', "hadn't", 'bought', 'animal', 'products,', 'poaching', 'would', 'have', 'decreased.'],
          a: "If tourists hadn't bought animal products, poaching would have decreased.",
          extra: ['did', 'not'],
          tip: "Use 'hadn't bought' for the unreal past condition."
        },
        {
          type: 'order',
          q: 'Si la gente hubiera actuado antes, no habríamos perdido tantas especies.',
          words: ['If', 'people', 'had', 'acted', 'earlier,', 'we', "wouldn't", 'have', 'lost', 'so', 'many', 'species.'],
          a: "If people had acted earlier, we wouldn't have lost so many species.",
          extra: ['would', 'act'],
          tip: "The negative result is 'wouldn't have lost'."
        }
      ]
    },
    {
      id: 'regrets-set3',
      title: "Set 3: Personal Regrets (Evaluation)",
      context: `*Arrepentimientos personales*


En un podcast, varias personas hablan de oportunidades perdidas y decisiones que habrían cambiado su vida.
Tu objetivo: Elegir la estructura correcta del tercer condicional y evitar errores frecuentes.


*Personal Regrets*


In a podcast, several people talk about missed opportunities and decisions that would have changed their lives.
Your goal: Choose accurate third conditional structures and avoid common mistakes.`,
      questions: [
        {
          type: 'mc',
          q: 'If I _____ that job offer, I would have moved abroad.',
          options: ['had accepted', 'accepted', 'would accept'],
          a: 'had accepted',
          tip: "Use 'If + past perfect' for an unreal past condition."
        },
        {
          type: 'mc',
          q: 'GRAMMAR TRAP: If she had studied harder, she _____ the exam.',
          options: ['would have passed', 'would passed', 'had passed'],
          a: 'would have passed',
          tip: "Use 'would have + past participle' in the main clause."
        },
        {
          type: 'input',
          q: 'If I had known about the concert, I _____ have bought tickets.',
          a: 'would',
          alts: ['would'],
          tip: "The full structure is 'would have bought'.",
          successTip: 'Exactly! “Would have bought” expresses an unreal past result. 🎟️'
        },
        {
          type: 'mc',
          q: 'Which sentence is grammatically CORRECT?',
          options: [
            'If I had taken the opportunity, I would have missed out on a great experience.',
            'If I would have taken the opportunity, I would have missed out on a great experience.',
            'If I had took the opportunity, I would have missed out on a great experience.'
          ],
          a: 'If I had taken the opportunity, I would have missed out on a great experience.',
          tip: "Use 'had taken', not 'had took'. Do not use 'would have' in the if-clause."
        },
        {
          type: 'match',
          q: 'Match each regret with the best third conditional sentence:',
          pairs: [
            { left: 'I did not apply for the scholarship.', right: 'If I had applied, I might have studied in Canada.' },
            { left: 'I said something unkind.', right: 'If I had thought before speaking, I would not have hurt my friend.' },
            { left: 'I missed the opportunity to travel.', right: 'If I had saved money, I would have travelled with my friends.' },
            { left: 'I changed my mind too late.', right: 'If I had decided earlier, I would have joined the course.' }
          ],
          a: 'completed',
          successTip: 'Excellent! You can express regrets clearly and naturally. 💭'
        },
        {
          type: 'mc',
          q: 'If they _____ earlier, they would not have missed the opportunity.',
          options: ['had acted', 'acted', 'would have acted'],
          a: 'had acted',
          tip: "The if-clause uses the past perfect: 'had acted'."
        },
        {
          type: 'input',
          q: 'Looking back on my life, I wish I had not m_____ out on that opportunity.',
          a: 'missed',
          alts: ['missed'],
          tip: "The phrasal verb means to lose the chance to do or experience something.",
          successTip: 'Correct! “Miss out on” is perfect for talking about missed opportunities. 🌟'
        },
        {
          type: 'mc',
          q: 'Which expression means that it is easy to understand a situation after it happens?',
          options: ['Hindsight is 20/20', 'Cry over spilled milk', 'Change my mind'],
          a: 'Hindsight is 20/20',
          tip: "Use 'Hindsight is 20/20' when someone understands a past situation better now."
        },
        {
          type: 'mc',
          q: 'If I _____ more confident, I would have spoken to the manager about the mistake.',
          options: ['had been', 'was', 'would have been'],
          a: 'had been',
          tip: "For the verb 'be' in a third conditional if-clause, use 'had been'."
        },
        {
          type: 'mc',
          q: 'The final test: Which sentence is UNNATURAL in English?',
          options: [
            'If I had known, I would have done things differently.',
            'If I would have known, I would have done things differently.',
            'If I had known, I might have done things differently.'
          ],
          a: 'If I would have known, I would have done things differently.',
          successTip:
            "Exactly! Avoid 'If I would have known'. Use 'If I had known' in the if-clause. Congratulations! 🏆"
        }
      ]
    }
  ]
};

// Merges the 3 sets into a single 30-question lesson.
// Each set keeps its context as a reading screen (passage).
// Both successTip and successtip are written so it works with either spelling.
// Alias de compatibilidad retrospectiva
export const THIRD_CONDITIONAL_UNIT = WEEKLY_UNIT;

export const WEEKLY_QUIZ_SET = {
  id: 'weekly-third-conditional-1',
  title: 'Weekly Quiz: Third Conditional',
  questions: WEEKLY_UNIT.sets.flatMap((s) => [
    {
      type: 'passage',
      title: s.title,
      passage: s.context,
      isAutoIntro: true,
      id: `intro_${s.id}`
    },
    ...s.questions.map((q) => {
      const success = q.successTip || q.successtip;
      return success
        ? { ...q, successTip: success, successtip: success }
        : q;
    })
  ])
};